import { Box, CircularProgress, Typography, styled } from "@mui/material";
import React, { useState } from "react";
import { MessageProps } from "../../../interfaces/all";
import { resolveMediaUrl } from "../../../utils/media";
import MessageAvatar from "./MessageAvatar";
import MessageMetaInfo from "./MessageMetaInfo";
import { MessageContainer } from "./styles";

interface ImageMessageProps extends MessageProps {
  showAvatar?: boolean;
  showDate?: boolean;
}

const ImageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMine",
})<{ isMine: boolean }>(({ isMine }) => ({
  maxWidth: "300px",
  borderRadius: "8px",
  overflow: "hidden",
  backgroundColor: isMine ? "#dcf8c6" : "#fff",
  boxShadow: "0 1px 0.5px rgba(0, 0, 0, 0.13)",
  cursor: "pointer",
  position: "relative",
  transition: "transform 0.2s",
  "&:hover": {
    transform: "scale(1.02)",
  },
}));

const ImageWrapper = styled(Box)({
  position: "relative",
  minWidth: "280px",
  minHeight: "200px",
  backgroundColor: "#f0f2f5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px 8px 0 0",
  overflow: "hidden",
});

const StyledImage = styled("img", {
  shouldForwardProp: (prop) => prop !== "loaded",
})<{ loaded: boolean }>(({ loaded }) => ({
  width: "100%",
  height: "auto",
  display: "block",
  maxHeight: "400px",
  objectFit: "contain",
  opacity: loaded ? 1 : 0,
  transition: "opacity 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
}));

const ErrorContainer = styled(Box)({
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
  background: "#f8f9fa",
  borderRadius: "8px",
});

const ImageMessage: React.FC<ImageMessageProps> = ({
  message,
  showAvatar = true,
  showDate = true,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const isFromClient = message.sentBy === "client";
  const currentReadStatus = message.readStatus || { isRead: false };
  const content = message.content as any;
  const text =
    content.text || content.caption || content.imageData?.caption || "";

  const rawUrl =
    content.image?.url ||
    content.image?.mediaUrl ||
    content.imageData?.url ||
    content.imageData?.originalUrl ||
    (typeof content.image === "string" ? content.image : "");

  const imageUrl = resolveMediaUrl(rawUrl);

  const handleImageClick = () => {
    if (imageUrl) {
      window.open(imageUrl, "_blank");
    }
  };

  const renderContent = () => {
    if (error) {
      return (
        <ErrorContainer>
          <Typography variant="caption" color="error">
            {content.imageData?.error ||
              content.image?.error ||
              "Erro ao carregar imagem"}
          </Typography>
        </ErrorContainer>
      );
    }

    return (
      <Box
        sx={{ position: "relative", display: "flex", flexDirection: "column" }}
      >
        <ImageWrapper>
          {loading && (
            <Box
              sx={{
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                inset: 0,
                zIndex: 2,
              }}
            >
              <CircularProgress size={24} sx={{ color: "#00a884" }} />
            </Box>
          )}
          {imageUrl && (
            <StyledImage
              src={imageUrl}
              alt="Mensagem de imagem"
              loading="lazy"
              loaded={!loading}
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false);
                setError(true);
                console.error(
                  "❌ [ImageMessage] Erro ao carregar imagem:",
                  imageUrl,
                );
              }}
              onClick={handleImageClick}
            />
          )}
        </ImageWrapper>

        {text && (
          <Box sx={{ p: 1, pb: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.875rem",
                color: "inherit",
                wordBreak: "break-word",
              }}
            >
              {text}
            </Typography>
          </Box>
        )}

        <MessageMetaInfo
          message={message}
          showDate={showDate}
          isFromClient={isFromClient}
          readStatus={currentReadStatus}
        />
      </Box>
    );
  };

  return (
    <MessageContainer isFromClient={isFromClient} showTriangle={false}>
      {isFromClient ? (
        <>
          <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
          <ImageContainer isMine={false}>{renderContent()}</ImageContainer>
        </>
      ) : (
        <>
          <ImageContainer isMine={true}>{renderContent()}</ImageContainer>
          <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
        </>
      )}
    </MessageContainer>
  );
};

export default ImageMessage;
